import { Dict } from "@/app/types"
import { isString } from "@/app/utils"

export class Calculation {
  public static readonly CALCNAMES: Dict<string> = {
    AVG: "Average",
    MED: "Median",
    P25: "25th Percentile",
    MIN: "Minimum",
    MAX: "Maximum",
  }

  public static readonly CALCS = Object.keys(Calculation.CALCNAMES)

  public static readonly CALC_SHORTNAMES: Dict<string> = {
    AVG: "Avg",
    MED: "Med",
    P25: "25%",
    MIN: "Min",
    MAX: "Max",
  }

  public static readonly CALCINFO: Dict<string> = {
    AVG: "The arithmetic mean, calculated as the sum of all the numbers in a list divided by the number of items in that list. The average can summarize the central tendency of a set of values with a single number, but it can also be skewed by the presence of outliers.",
    P25: "The median value of the top half of a data sequence sorted by value. It marks the minimum value of the top quartile and signifies the entry point for superior performance in any given metric.",
    MED: "The middle value in a list of numbers ranked by size. If there is an odd number of values, median is the one in the middle, with same number above and below. If there is an even number of values, the median is calculated as midpoint of middle pair. The median is sometimes used when outliers in a data sequence might skew the average of the values.",
    MIN: "The smallest number in a series of values.",
    MAX: "The largest number in a series of values.",
  }

  private calc: string

  constructor(calc: string) {
    if (!Calculation.isValidCalc(calc)) {
      throw new Error(`Invalid calculation: ${calc}`)
    }
    this.calc = calc
  }

  static isValidCalc(c: string): boolean {
    return isString(c) && Calculation.CALCS.includes(c)
  }

  calculation() {
    return this.calc
  }

  getCalcInfo(type: "name" | "short" | "info", calcCode?: string): string {
    const calcInfos = {
      name: Calculation.CALCNAMES,
      short: Calculation.CALC_SHORTNAMES,
      info: Calculation.CALCINFO,
    }
    const code = calcCode || this.calc
    return calcInfos[type][code]
  }

  equals(other: Calculation): boolean {
    return other !== null && this.calc === other.calc
  }

  getCalcPillText(c: string): string {
    const names: Dict<string> = Calculation.CALC_SHORTNAMES
    if (!Calculation.isValidCalc(c))
      throw new Error(`Unexpected calc:${c}, expected:${names}`)
    return names[c]
  }

  getCalcName(c: string): string {
    const names: Dict<string> = Calculation.CALCNAMES
    if (!Calculation.isValidCalc(c))
      throw new Error(`Unexpected calc:${c}, expected:${names}`)
    return names[c]
  }
}

export namespace Calculation {
  export const AVG = "Average"
  export const MAX = "Maximum"
  export const MIN = "Minimum"
  export const MED = "Median"
  export const P25 = "25th Percentile"

  export const CALC_NAMES = [AVG, P25, MED, MIN, MAX]
}
